import { useTranslation } from "../../localization/useTranslation";
import { ProcessingActivityPlanTabId, ProcessingActivityTabCountsViewModel, ProcessingActivityTopBarViewModel } from "./ProcessingActivityOverview.types";
import { useState } from "react";
import { useUrlProvider } from "../../useUrlProvider";
import { useStateUrlParamsBoolean } from "../../common/hooks/useStateUrlParams";
import { useLocalStorageTab } from "@common/hooks/useLocalStorageTab";
import { RiskAssessmentScoreViewModel } from "../../riskAssessment/RiskAssessment.types";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useNavigate } from "react-router";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider";
import { NumberOfDataSubjectsEnum } from "../ProcessingActivity.types";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { canSubmitProcessingActivityAsResearcher } from "../ProcessingActivity.hooks";

import { useDotLegalQuery, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useProcessingActivitySharingsQuery } from "./processingActivitySharingsTab/ProcessingActivitySharingsTab.hooks";

export function useProcessingActivityOverviewDataMapping(activityId: string | undefined) {
    const navigate = useNavigate();
    const { customerId } = useUserContext();

    const [selectedTab, setSelectedTab] = useLocalStorageTab(
        `processing-activity-plan-tab-${customerId}`,
        processingActivityPlanTabMapping,
        ProcessingActivityPlanTabId.Sharings
    );
    const { getProcessingActivitiesOverviewUrl } = useUrlProvider();
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { hasEnterpriseRiskManagementAccess } = usePermissionsProvider();
    const { addonsFeatures } = usePlanContext();

    const topBarQuery = useDotLegalQueryWithKey<ProcessingActivityTopBarViewModel>(
        "processingActivityTopBar" + activityId,
        `/ProcessingActivityOverview/${activityId}/top-bar`
    );
    const sharingsQuery = useProcessingActivitySharingsQuery(activityId);
    const tabCountsQuery = useDotLegalQueryWithKey<ProcessingActivityTabCountsViewModel>(
        "processingActivityTabCounts" + activityId,
        `/ProcessingActivityOverview/${activityId}/tab-counts`
    );
    let riskAssessmentScoreUrl = `/processingActivityRiskAssessment/${activityId}/scores`;
    let riskScoreQuery = useDotLegalQuery<RiskAssessmentScoreViewModel>(riskAssessmentScoreUrl, {
        enabled: permissions.canManageRiskAssessment && !hasEnterpriseRiskManagementAccess,
    });

    const [showAuditLogDialog, setShowAuditLogDialog] = useStateUrlParamsBoolean("auditlog", false);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showNoteDialog, setShowNoteDialog] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    const [showProcessingActivityReportDialog, setShowProcessingActivityReportDialog] = useState(false);
    const [showRiskAreasDialog, setShowRiskAreasDialog] = useState(false);
    const [showPolicyDialog, setShowPolicyDialog] = useState(false);

    const topBar = topBarQuery.data;

    const canSubmitProcessingActivity =
        addonsFeatures.research && canSubmitProcessingActivityAsResearcher(permissions.processingActivityStatusManagement, topBar?.processingActivityStatus);

    const tabCounts = {
        sharings: sharingsQuery.data?.sharings.length,
        dataCategories: tabCountsQuery.data?.dataCategories,
        assets: tabCountsQuery.data?.assets,
        documents: tabCountsQuery.data?.documents,
        audits: tabCountsQuery.data?.audits,
        classifications: tabCountsQuery.data?.classifications,
    };

    const onProcessingActivityDeleteSuccess = () => {
        navigate(getProcessingActivitiesOverviewUrl());
    };

    return {
        topBar,
        isTopBarLoading: topBarQuery.isLoading,
        refetchTopBar: topBarQuery.refetch,
        tabCounts,
        refetchTabCounts: tabCountsQuery.refetch,
        showNoteDialog,
        setShowNoteDialog,
        hasEnterpriseRiskManagementAccess,
        riskAssessmentData: riskScoreQuery.data,
        riskAssessmentLoading: riskScoreQuery.isLoading,
        showDeleteDialog,
        setShowDeleteDialog,
        showRiskAreasDialog,
        setShowRiskAreasDialog,
        onProcessingActivityDeleteSuccess,
        showPolicyDialog,
        setShowPolicyDialog,
        showStatusDialog,
        setShowStatusDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
        showProcessingActivityReportDialog,
        setShowProcessingActivityReportDialog,
        canSubmitProcessingActivity,
        selectedTab,
        setSelectedTab,
    };
}

const processingActivityPlanTabMapping: Record<number, ProcessingActivityPlanTabId> = {
    0: ProcessingActivityPlanTabId.Sharings,
    1: ProcessingActivityPlanTabId.DataCategories,
    2: ProcessingActivityPlanTabId.Assets,
    3: ProcessingActivityPlanTabId.Documents,
    4: ProcessingActivityPlanTabId.Audits,
    5: ProcessingActivityPlanTabId.Classifications,
    6: ProcessingActivityPlanTabId.Tasks,
};

export function getNumberOfDataSubjects(
    translateDataSubjectsEnum: (dataSubject: string) => string,
    numberOfSubjects?: NumberOfDataSubjectsEnum,
    noOfSubjects?: number
) {
    if (numberOfSubjects) {
        if (numberOfSubjects === NumberOfDataSubjectsEnum.UserEnteredNumberOfSubjects) {
            return noOfSubjects;
        }

        return translateDataSubjectsEnum(numberOfSubjects!.toString());
    }

    return 0;
}
